import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import baseAPIService from '../services/baseapiservice';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
} from '@mui/icons-material';

interface Tenant {
  /** ds_uda_lst_t.uda_cd when this tenant is a UDA; `name` is then the UDA name. */
  udaCode?: string | null;
  id: number;
  name: string;
  code: string;
}

interface Assignment {
  assignment_id: number;
  department_id: number;
  department_name: string;
  designation_id: number;
  designation_name: string;
  position_id: number;
  position_name: string;
  from_date: string;
  to_date: string | null;
}

/**
 * One choice in the "which role do you want to open?" picker shown at login.
 * An employee reaches a role through an assignment, so a user holding two
 * assignments (e.g. Junior Assistant -> UDA Clerk and Administrative Officer ->
 * UDA Admin) gets two entries, each opening a different set of modules.
 */
interface SelectableRole {
  assignment_id: number;
  is_primary: boolean;
  position_id: number;
  position_name: string;
  department_id?: number;
  department_name?: string;
  designation_id?: number;
  designation_name?: string;
  rle_id: number;
  rle_nm: string;
  prfle_id: number;
  prfle_nm: string;
  dflt_url_tx?: string | null;
  from_date?: string;
  to_date?: string | null;
}

interface User {
  uid: string;
  name: string;
  /**
   * The login id. Carried because `name` is the display name off
   * `microservice.ds_usr_lst_t.name`, which is nullable — screens that STAMP the
   * signed-in official onto a record (the Inspections form's inspector of
   * record, for one) need something that is always present to fall back to.
   */
  username?: string;
  roleId: number;
  roleName?: string;     // usr_rle_lst_t.rle_nm — the RBAC role, shown next to the user name in the header
  roleType?: string | null; // usr_rle_lst_t.rle_type, e.g. 'state user' — gates the asset dashboard heat map
  ulbId: number;
  udaId?: number;        // ds_uda_lst_t.uda_id derived from tenant code (null for state users)
  udaName?: string;      // ds_uda_lst_t.uda_nm — shown next to the user name in the header
  udaCode?: string;      // ds_uda_lst_t.uda_cd — short code (e.g. 'RUDA'), the portal header title
  type: string;
  lastLoginDate?: string;
  tnt_usr_id?: number; // Tenant user ID from microservice.eg_user
  primaryAssignment?: {
    assignmentId?: number;
    departmentId?: number;
    departmentName?: string;
    designationId?: number;
    designationName?: string;
    positionId?: number;
    positionName?: string;
  };
  primaryBoundary?: HierarchyData;
  tenants?: Tenant[];
  isStateRegionalUser?: boolean;
  tenantId?: string;
  siteId?: string; // Portal site ID (e.g., 'APENC', 'APUFIDC') for feature restrictions
  employeeId?: string; // For APUFIDC users
}

interface HierarchyData {
  // Singular - first/primary boundary at each level
  state?: Boundary | null;
  regional?: Boundary | null;
  ulb?: Boundary | null;
  ward?: Boundary | null;
  sachivalayam?: Boundary | null;
  // Plural arrays - all boundaries at each level
  states?: Boundary[];
  regionals?: Boundary[];
  ulbs?: Boundary[];
  wards?: Boundary[];
  sachivalayams?: Boundary[];
}

interface Boundary {
  id: number;
  name: string;
  code: string;
  type?: string;
  boundaryTypeId?: number;
  hierarchyTypeId?: number;
  tenantId?: string;  // Required for ward/sachivalayam to identify parent ULB
}

// Permission Denied Modal Types
interface PermissionHolder {
  userId: number;
  userName: string;
  email: string;
  roleName: string;
}

interface RoleWithPermission {
  roleId: number;
  roleName: string;
  roleDescription?: string;
  permissions?: {
    canCreate: boolean;
    canRead: boolean;
    canUpdate: boolean;
    canDelete: boolean;
  };
}

export interface PermissionHoldersData {
  rolesWithPermission?: RoleWithPermission[];
  usersWithPermission?: PermissionHolder[];
  adminUsers?: PermissionHolder[];
  canRequestAccess?: boolean;
  contactMessage?: string;
}

export interface PermissionDeniedData {
  tableName: string;
  permission?: 'create' | 'read' | 'update' | 'delete';
  holders?: PermissionHoldersData;
  message?: string;
}

export interface LoginPayload {
  username: string;
  password: string;
  captcha: string;
  captcha_id: number;
}

export interface LoginCrdaPayload {
  username: string;
  password: string;
  salt_key: string;
  captcha: string;
  captcha_id: number;
}
export interface LoginApufidcPayload {
  username: string;
  password: string;
  site_id?: string; // Portal site ID (e.g., 'APENC', 'APUFIDC')
  captcha_id: number;
  captcha: string;
}

export interface ForgotPasswordPayload {
  username: string;
  email: string;
  mobileNumber: string;
  whatsappNumber: string;
  captcha: string;
  captcha_id: number;
  channel: 'email' | 'sms' | 'whatsapp';
}

export interface CaptchaData {
  cptch_id: number;
  salt_ky: string;
  cptch_img_tx: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  authType: 'erp' | 'crda' | 'apufidc' | null;
  login: (data: LoginPayload) => Promise<void>;
  loginCrda: (data: LoginCrdaPayload) => Promise<void>;
  loginApufidc: (data: LoginApufidcPayload) => Promise<void>;
  captcha: () => Promise<CaptchaData | null>;
  externalCaptcha: () => Promise<CaptchaData | null>;
  logout: () => Promise<void>;
  selectTenant: (id: number, by?: 'tenant' | 'uda') => Promise<void>;
  // Login UDA picker: bind the session to a UDA, then continue to the role step or the app.
  selectUda: (tenantId: number) => Promise<void>;
  /** UDAs awaiting the sign-in choice; null when there is nothing to ask. */
  pendingUdaSelection: Tenant[] | null;
  /** True while a hostname-selected UDA is being bound during login. */
  isSelectingUda: boolean;
  /** Abandon that choice — signs the user out rather than picking for them. */
  cancelUdaSelection: () => Promise<void>;
  selectAssignment: (assignmentId: number, roleId?: number) => Promise<void>;
  assignments: Assignment[];
  // Roles the logged-in user may open. Length > 1 means the login role picker is shown.
  roles: SelectableRole[];
  // Apply a role chosen in the picker and land on that role's own start page.
  selectRole: (assignmentId: number, roleId: number) => Promise<void>;
  requestPasswordReset: (data: ForgotPasswordPayload) => Promise<{ success: boolean; message: string; otpRequired?: boolean; channel?: string }>;
  lookupForgotUser: (username: string) => Promise<{ exists: boolean; email?: string; mobile?: string; name?: string }>;
  validateResetToken: (token: string) => Promise<boolean>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
  verifyResetOtp: (username: string, otp: string) => Promise<boolean>;
  resetPasswordByOtp: (username: string, otp: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
  isAuthenticated: boolean;
  isCrdaAuthenticated: boolean;
  isApufidcAuthenticated: boolean;
  // Permission denied modal
  showPermissionDenied: (data: PermissionDeniedData) => void;
  hidePermissionDenied: () => void;
}

export type { Tenant, Assignment, SelectableRole };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const REDIRECT_URL_KEY = 'auth_redirect_url';

/** A UDA subdomain already answers the UDA question for a multi-UDA employee. */
const getPortalUdaSlug = (): string | null => {
  if (typeof window === 'undefined') return null;
  const firstLabel = window.location.hostname.split('.')[0]?.trim().toLowerCase();
  return firstLabel && firstLabel !== 'localhost' && firstLabel !== 'dtcp'
    ? firstLabel
    : null;
};

const tenantMatchesPortal = (tenant: Tenant, portalSlug: string): boolean => {
  const searchable = `${tenant.code || ''} ${tenant.name || ''}`.toLowerCase();
  const slug = portalSlug.toLowerCase();
  // Supports legacy codes such as ap.uda_amuda and names ending in (AMUDA).
  return searchable.includes(`uda_${slug}`)
    || searchable.includes(`(${slug})`)
    || searchable.includes(slug);
};

export const redirectToUserMainPage = () => {
  const preservedUrl = localStorage.getItem(REDIRECT_URL_KEY);
  if (preservedUrl) {
    localStorage.removeItem(REDIRECT_URL_KEY);
    return (window.location.href = preservedUrl);
  }

  const landingPageUrl = localStorage.getItem('landing_page_url');
  window.location.href = landingPageUrl || '/';
};

export const redirectToLoginPage = (preserveCurrentUrl: boolean = false) => {
  // Get auth type before clearing it to determine which login page to use
  const authType = localStorage.getItem(AUTH_TYPE_KEY);

  if (preserveCurrentUrl) {
    const currentPath = window.location.pathname + window.location.search;
    if (!currentPath.startsWith('/login') && !currentPath.startsWith('/app/apufidc/login')) {
      localStorage.setItem(REDIRECT_URL_KEY, currentPath);
    }
  }

  // Redirect to appropriate login page based on auth type
  if (authType === 'apufidc') {
    window.location.href = '/app/apufidc/login';
  } else if (authType === 'crda') {
    window.location.href = '/app/use-cases/login';
  } else {
    window.location.href = '/login';
  }
};
interface AuthProviderProps {
  children: ReactNode;
}

const AUTH_TYPE_KEY = 'auth_type';

/**
 * Clear all session cookies with the given name from all paths
 * This is necessary because the server might set cookies with different paths
 * and they don't overwrite each other, causing session confusion
 */
const clearAllSessionCookies = (cookieName?: string) => {
  // Clear all possible session cookie names
  // Note: Different domains (localhost, UAT, PROD) are automatically isolated by browser
  const cookieNames = cookieName ? [cookieName] : [
    'maudInfraGeo.sid',   // ERP auth
    'crdaAuth.sid',       // CRDA auth
    'apufidcAuth.sid',    // APUFIDC auth
    'connect.sid'         // Fallback/legacy
  ];
  // Extended list of all possible paths where session cookies might be set
  const paths = [
    '/',
    '/apiv1',
    '/apiv1/',
    '/apiv1/auth',
    '/apiv1/auth/',
    '/apiv1/auth/login',
    '/apiv1/erp-admin',
    '/apiv1/erp-auth',
    '/apiv1/erp-auth/login',
    '/apiv1/erp-auth/verify',
    '/apiv1/crda-auth',
    '/apiv1/crda-auth/login',
    '/apiv1/apufidc-auth',
    '/apiv1/apufidc-auth/login'
  ];

  // Also try to clear from current path and its parent paths
  const currentPath = window.location.pathname;
  const pathParts = currentPath.split('/').filter(Boolean);
  let buildPath = '';
  pathParts.forEach(part => {
    buildPath += '/' + part;
    if (!paths.includes(buildPath)) {
      paths.push(buildPath);
    }
  });

  // Clear all cookie names from all paths (iterate multiple times to ensure cleanup)
  for (let i = 0; i < 3; i++) {
    cookieNames.forEach(name => {
      paths.forEach(path => {
        // Try without domain
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
        // Try with current domain
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${window.location.hostname}`;
        // Try with domain starting with dot (for subdomains)
        if (window.location.hostname !== 'localhost') {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=.${window.location.hostname}`;
        }
      });
    });
  }

};

/**
 * Clear all auth-related local storage items
 */
const clearAllAuthStorage = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user_data');
  localStorage.removeItem('user_assignments');
  localStorage.removeItem(USER_ROLES_KEY);
  localStorage.removeItem('x-access-token');
  localStorage.removeItem(AUTH_TYPE_KEY);
  localStorage.removeItem('user_permissions_cache');
  localStorage.removeItem('user_permissions_timestamp');
};

/**
 * Roles the user may open, cached so the picker survives a page refresh while the
 * user is still sitting on it (they have a valid token at that point, just no choice made).
 */
const USER_ROLES_KEY = 'user_roles';

/** Route of the "select your role" interstitial shown between login and the app. */
export const SELECT_ROLE_PATH = '/select-role';

/** Route of the "select your UDA" interstitial, shown before the role picker. */
export const SELECT_UDA_PATH = '/select-uda';

const readStoredRoles = (): SelectableRole[] => {
  try {
    const saved = localStorage.getItem(USER_ROLES_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [authType, setAuthType] = useState<'erp' | 'crda' | 'apufidc' | null>(null);
  /**
   * The UDAs an employee must choose between before the session is bound to one.
   * Non-null only between a successful password check and that choice — an employee who
   * works at a single UDA never has it set, and state/regional users never do either
   * (they work across UDAs and switch from the header instead).
   */
  const [pendingUdaSelection, setPendingUdaSelection] = useState<Tenant[] | null>(null);
  const [isSelectingUda, setIsSelectingUda] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [roles, setRoles] = useState<SelectableRole[]>(readStoredRoles);

  // Permission denied modal state
  const [permissionDeniedOpen, setPermissionDeniedOpen] = useState(false);
  const [permissionDeniedData, setPermissionDeniedData] = useState<PermissionDeniedData | null>(null);
  const [requestReason, setRequestReason] = useState('');
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Show permission denied modal - memoized to prevent race conditions
  const showPermissionDenied = useCallback((data: PermissionDeniedData) => {
    setPermissionDeniedData(data);
    setPermissionDeniedOpen(true);
    setRequestSuccess(null);
    setRequestError(null);
  }, []);

  // Hide permission denied modal
  const hidePermissionDenied = useCallback(() => {
    setPermissionDeniedOpen(false);
    setPermissionDeniedData(null);
    setRequestDialogOpen(false);
    setRequestReason('');
    setRequestSuccess(null);
    setRequestError(null);
  }, []);

  // Handle access request submission
  const handleRequestAccess = async () => {
    if (!permissionDeniedData) return;

    setRequestSubmitting(true);
    setRequestError(null);

    try {
      const response: any = await baseAPIService.post('/audit/request-access', {
        tableName: permissionDeniedData.tableName,
        permission: permissionDeniedData.permission || 'read',
        reason: requestReason,
      });

      if (response?.data?.status === 200) {
        setRequestSuccess(response.data.data?.message || 'Access request sent successfully!');
        setRequestDialogOpen(false);
        setRequestReason('');
      } else {
        setRequestError(response?.data?.message || 'Failed to send request');
      }
    } catch (err: any) {
      console.error('Failed to request access:', err);
      setRequestError(err?.response?.data?.message || 'Failed to send access request');
    } finally {
      setRequestSubmitting(false);
    }
  };

  useEffect(() => {
    // Check for existing auth on app load and validate token with server
    const validateSession = async () => {
      const savedToken = localStorage.getItem('x-access-token');
      const savedUser = localStorage.getItem('user_data');
      const savedAuthType = localStorage.getItem(AUTH_TYPE_KEY) as 'erp' | 'crda' | 'apufidc' | null;

      // console.log('[validateSession] Starting validation...');
      // console.log('[validateSession] savedToken exists:', !!savedToken);
      // console.log('[validateSession] savedUser exists:', !!savedUser);
      // console.log('[validateSession] savedAuthType:', savedAuthType);

      if (savedToken && savedUser) {
        try {
          // Determine which auth endpoint to use based on auth type
          // Use apufidc-auth for APUFIDC users (verifies against apufidc_enc_db schema)
          let verifyEndpoint = 'erp-auth/verify';
          if (savedAuthType === 'crda') {
            verifyEndpoint = 'crda-auth/verify';
          } else if (savedAuthType === 'apufidc') {
            verifyEndpoint = 'apufidc-auth/verify';
          }

          // console.log('[validateSession] Calling verify endpoint:', verifyEndpoint);

          // Validate token with server (interceptor will add x-access-token header automatically)
          const response: any = await baseAPIService.post(verifyEndpoint, {});

          // console.log('[validateSession] Verify response:', response);

          if (response?.status === 200 && response?.data?.status === 200) {
            // Token is valid, restore session
            const userData = JSON.parse(savedUser);
            const savedAssignments = localStorage.getItem('user_assignments');
            setToken(savedToken);
            setUser(userData);
            setAssignments(savedAssignments ? JSON.parse(savedAssignments) : []);
            setRoles(readStoredRoles());
            setAuthType(savedAuthType);
            // console.log('[validateSession] Session restored successfully');

          } else {
            // Token is invalid, clear stored data
            // console.log('[validateSession] Token validation failed - invalid response');
            throw new Error('Token validation failed');
          }
        } catch (error: any) {
          // console.error('[validateSession] Session validation failed:', error);

          // Check if this is a network error (server unavailable) vs auth error
          const isNetworkError = !error?.response && (
            error?.code === 'ERR_NETWORK' ||
            error?.code === 'ECONNABORTED' ||
            error?.message?.includes('Network Error') ||
            error?.message?.includes('timeout') ||
            !navigator.onLine
          );

          const isAuthError = error?.response?.status === 401 ||
            error?.response?.status === 403 ||
            error?.response?.status === 406;

          if (isNetworkError) {
            // Server is unavailable (possibly restarting) - keep user logged in with cached data
            // The token might still be valid once server is back up
            console.warn('Server unavailable during token verification. Keeping cached session.');
            try {
              const userData = JSON.parse(savedUser);
              const savedAssignments = localStorage.getItem('user_assignments');
              setToken(savedToken);
              setUser(userData);
              setAssignments(savedAssignments ? JSON.parse(savedAssignments) : []);
              setRoles(readStoredRoles());
              setAuthType(savedAuthType);
            } catch (parseError) {
              console.error('Failed to parse cached user data:', parseError);
              // Only clear if we can't even parse cached data
              localStorage.removeItem('auth_token');
              localStorage.removeItem('user_data');
              localStorage.removeItem('user_assignments');
              localStorage.removeItem('x-access-token');
              localStorage.removeItem(AUTH_TYPE_KEY);
            }
          } else if (isAuthError) {
            // Token is actually invalid - clear stored data
            console.warn('Token is invalid. Clearing session.');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            localStorage.removeItem('x-access-token');
            localStorage.removeItem(AUTH_TYPE_KEY);
          } else {
            // Unknown error - be conservative and keep the session
            // This prevents logout on unexpected errors
            console.warn('Unknown error during verification. Keeping cached session.');
            try {
              const userData = JSON.parse(savedUser);
              const savedAssignments = localStorage.getItem('user_assignments');
              setToken(savedToken);
              setUser(userData);
              setAssignments(savedAssignments ? JSON.parse(savedAssignments) : []);
              setRoles(readStoredRoles());
              setAuthType(savedAuthType);
            } catch (parseError) {
              console.error('Failed to parse cached user data:', parseError);
              localStorage.removeItem('auth_token');
              localStorage.removeItem('user_data');
              localStorage.removeItem('user_assignments');
              localStorage.removeItem('x-access-token');
              localStorage.removeItem(AUTH_TYPE_KEY);
            }
          }
        }
      }
      setIsLoading(false);
    };

    validateSession();
  }, []);

  const captcha = async () => {
    try {
      const response: any = await baseAPIService.get(`/auth/login/captcha`);
      if (response.status === 200 && response.data?.status === 200) {
        return {
          cptch_id: response.data?.data?.cptch_id || 0,
          salt_ky: response.data?.data?.salt_ky || '',
          cptch_img_tx: response.data?.data?.cptch_img_tx || ''
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting captcha:', error);
      return null;
    }
  };

  const externalCaptcha = async () => {
    try {
      const apiKey = import.meta.env.VITE_EXTERNAL_API_KEY || 'ds-abc6ba278b6ac48ca38d7d10c4ead398-mkxmv2zo';
      const response: any = await baseAPIService.get('erp-auth/external/captcha', {
        headers: {
          'x-api-key': apiKey,
        },
      });
      if (response.status === 200 && response.data?.status === 200) {
        // External captcha returns: captcha_id, salt_key, captcha_image
        // Map to standard CaptchaData format: cptch_id, salt_ky, cptch_img_tx
        return {
          cptch_id: response.data?.data?.captcha_id || 0,
          salt_ky: response.data?.data?.salt_key || '',
          cptch_img_tx: response.data?.data?.captcha_image || ''
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting external captcha:', error);
      return null;
    }
  };

  const login = async (data: LoginPayload) => {
    const { username, password, captcha, captcha_id } = data;
    try {
      setIsLoading(true);

      // Clear any existing session cookies and auth storage before login
      clearAllSessionCookies();
      clearAllAuthStorage();

      const loginData = {
        username: username,
        password: password,
        captcha: captcha,
        captcha_id: captcha_id
      };

      const response: any = await baseAPIService.post('erp-auth/login', loginData);

      if (response?.data?.status === 200) {
        const resData = response?.data?.data;

        const accessToken = resData.token;

        const userData: User = {
          uid: resData.user.id,
          name: resData.user.name,
          username: resData.user.username,
          roleId: resData.user.roleId,
          roleName: resData.user.roleName,
          roleType: resData.user.roleType,
          ulbId: resData.user.ulbId,
          udaId: resData.user.udaId,
          udaName: resData.user.udaName,
          udaCode: resData.user.udaCode,
          type: resData.user.type,
          lastLoginDate: resData.user.lastLoginDate,
          tnt_usr_id: resData.user.tnt_usr_id,
          primaryAssignment: resData.user.primaryAssignment,
          primaryBoundary: resData.user.primaryBoundary,
          tenants: resData.tenants || [],
          isStateRegionalUser: resData.user.isStateRegionalUser || false,
          tenantId: resData.user.tenantId,
        };

        // Store token, user data, and assignments
        localStorage.setItem('x-access-token', accessToken);
        localStorage.setItem('user_data', JSON.stringify(userData));
        localStorage.setItem('user_assignments', JSON.stringify(resData.assignments || []));
        localStorage.setItem(USER_ROLES_KEY, JSON.stringify(resData.roles || []));
        localStorage.setItem(AUTH_TYPE_KEY, 'erp');
        // Use server landing page if it's a valid ERP route, otherwise default to dashboard
        const serverLandingUrl = resData.landingPageUrl;
        const landingPageUrl = (serverLandingUrl && serverLandingUrl.startsWith('/app/')) ? serverLandingUrl : '/app/maud-erp';
        localStorage.setItem('landing_page_url', landingPageUrl);

        setToken(accessToken);
        setUser(userData);
        setAssignments(resData.assignments || []);
        setRoles(resData.roles || []);
        setAuthType('erp');

        // The sign-in cascade narrows: person → UDA → role. Ask for the UDA first, because
        // the answer decides which assignments (and therefore which roles) even exist. Each
        // step is skipped whenever there is only one option, so a single-UDA employee with a
        // single role still goes straight from password to dashboard.
        if (resData.requiresUdaSelection) {
          const portalSlug = getPortalUdaSlug();
          const portalTenant = portalSlug
            ? (resData.tenants || []).find((tenant: Tenant) => tenantMatchesPortal(tenant, portalSlug))
            : null;

          if (portalTenant) {
            // The microsite hostname is the user's UDA choice. Re-issue the
            // token for that tenant and continue to role/landing navigation.
            setIsSelectingUda(true);
            try {
              await selectUda(portalTenant.id);
            } catch (error) {
              // Keep the authenticated session and fall back to the manual
              // picker if the automatic tenant binding cannot be completed.
              setIsSelectingUda(false);
              setPendingUdaSelection(resData.tenants || []);
              throw error;
            }
            return;
          }

          // Asked as a dialog over the sign-in page rather than a page redirect, so the
          // question reads as the last step of signing in instead of a new screen.
          // The session is already valid at this point (the token defaults to the
          // primary assignment); choosing a UDA re-issues it bound to that authority.
          setPendingUdaSelection(resData.tenants || []);
          return;
        }

        // An employee holding more than one role must say which one to open — the roles
        // map to different menu profiles, so the sidebar differs entirely between them.
        // The token issued above is already valid (it defaults to the primary assignment),
        // so the picker is a choice, not a second authentication step.
        if (resData.requiresRoleSelection) {
          window.location.href = SELECT_ROLE_PATH;
          return;
        }

        // Redirect to dashboard or assigned page
        redirectToUserMainPage();
      } else {
        throw new Error(response?.data?.message || 'Login failed');
      }
    } catch (error: any) {
      throw new Error(error?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const loginCrda = async (loginData: LoginCrdaPayload) => {
    try {
      setIsLoading(true);

      // Clear any existing session cookies and auth storage before login
      clearAllSessionCookies();
      clearAllAuthStorage();

      const response: any = await baseAPIService.post('crda-auth/login', loginData);

      if (response?.status === 200 && response?.data?.status === 200) {
        const resData = response?.data?.data;

        const accessToken = resData.token;

        const userData: any = {
          uid: resData.user.id,
          roleId: resData.user.roleId,
          roleName: resData.user.roleName,
          roleType: resData.user.roleType,
          type: resData.user.type,
          name: resData.user.name,
          username: resData.user.username,
          lastLoginDate: resData.user.lastLoginDate,
          tnt_usr_id: resData.user.tnt_usr_id,
          primaryAssignment: resData.user.primaryAssignment,
        };

        // Store token, user data, and assignments
        localStorage.setItem('x-access-token', accessToken);
        localStorage.setItem('user_data', JSON.stringify(userData));
        localStorage.setItem('user_assignments', JSON.stringify(resData.assignments || []));
        // CRDA sessions have no ERP role picker — drop any roles cached by a prior
        // ERP login so the header switcher does not show stale entries.
        localStorage.removeItem(USER_ROLES_KEY);
        setRoles([]);
        localStorage.setItem(AUTH_TYPE_KEY, 'crda');
        const landingPageUrl = resData.user.landingPageUrl || '/app/use-cases';
        localStorage.setItem('landing_page_url', landingPageUrl);

        setToken(accessToken);
        setUser(userData);
        setAssignments(resData.assignments || []);
        setAuthType('crda');


        // Redirect to dashboard or assigned page
        redirectToUserMainPage();
      } else {
        throw new Error(response?.data?.message || 'Login failed');
      }
    } catch (error: any) {
      throw new Error(error?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const loginApufidc = async (data: LoginApufidcPayload) => {
    try {
      setIsLoading(true);

      // Clear any existing session cookies and auth storage before login
      clearAllSessionCookies();
      clearAllAuthStorage();

      // Use external API for APUFIDC login - requires x-api-key header
      const apiKey = import.meta.env.VITE_EXTERNAL_API_KEY || 'ds-abc6ba278b6ac48ca38d7d10c4ead398-mkxmv2zo';
      const response: any = await baseAPIService.post('erp-auth/external/login', {
        username: data.username,
        password: data.password,
        site_id: data.site_id || null, // prvdr_ste_id from public portal
        captcha_id: data.captcha_id,
        captcha: data.captcha,
      }, {
        headers: {
          'x-api-key': apiKey,
        },
      });

      if (response?.status === 200 && response?.data?.status === 200) {
        const resData = response?.data?.data;

        const accessToken = resData.token;

        const userData: any = {
          uid: resData.user.uid || resData.user.id,
          roleId: resData.user.roleId,
          roleName: resData.user.roleName,
          roleType: resData.user.roleType,
          name: resData.user.name,
          username: resData.user.username,
          type: resData.user.type,
          employeeId: resData.user.employeeId,
          tenantId: resData.user.tenantId,
          tnt_usr_id: resData.user.tnt_usr_id,
          primaryAssignment: resData.user.primaryAssignment,
          isStateRegionalUser: resData.user.isStateRegionalUser,
          primaryBoundary: resData.user.primaryBoundary,
          tenants: resData.tenants || [],
          siteId: resData.user.siteId || data.site_id || null, // Portal site ID (prvdr_ste_id) for feature restrictions
        };

        // Store token, user data, and assignments
        localStorage.setItem('x-access-token', accessToken);
        localStorage.setItem('user_data', JSON.stringify(userData));
        localStorage.setItem('user_assignments', JSON.stringify(resData.assignments || []));
        // APUFIDC sessions have no ERP role picker — see the CRDA note above.
        localStorage.removeItem(USER_ROLES_KEY);
        setRoles([]);
        localStorage.setItem(AUTH_TYPE_KEY, 'apufidc');

        // Fetch landing page URL from APUFIDC schema using rle_id
        let landingPageUrl = '';
        const rleId = resData.user?.roleId;
        if (rleId) {
          try {
            const landingPageResponse: any = await baseAPIService.get(`apufidc-auth/landing-page-url?rle_id=${rleId}`);
            const apufidcLandingUrl = landingPageResponse?.data?.data?.landingPageUrl;
            if (apufidcLandingUrl) {
              const backendUrl = apufidcLandingUrl.startsWith('/')
                ? apufidcLandingUrl
                : `/${apufidcLandingUrl}`;
              // Only use backend URL if it's a valid APUFIDC route
              if (backendUrl.startsWith('/app/apufidc')) {
                landingPageUrl = backendUrl;
              } else if (backendUrl.startsWith('/app/maud-erp/apufidc')) {
                // Legacy route migration: convert old routes to new structure
                landingPageUrl = backendUrl.replace('/app/maud-erp/apufidc', '/app/apufidc');
              }
            }
          } catch (landingPageError) {
            console.warn('APUFIDC Login - Could not fetch landing page URL from APUFIDC schema:', landingPageError);
          }
        }
        localStorage.setItem('landing_page_url', landingPageUrl);

        // Clear any preserved redirect URLs that don't point to APUFIDC routes
        // This prevents APUFIDC users from being redirected to ERP routes they can't access
        const preservedUrl = localStorage.getItem(REDIRECT_URL_KEY);
        if (preservedUrl && !preservedUrl.startsWith('/app/apufidc')) {
          localStorage.removeItem(REDIRECT_URL_KEY);
        }

        setToken(accessToken);
        setUser(userData);
        setAssignments(resData.assignments || []);
        setAuthType('apufidc');

        // Redirect to APUFIDC dashboard or valid APUFIDC route
        redirectToUserMainPage();
      } else {
        throw new Error(response?.data?.err_message || response?.data?.message || 'Login failed');
      }
    } catch (error: any) {
      throw new Error(error?.response?.data?.err_message || error?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // A half-finished UDA choice must not survive into the next sign-in.
    setPendingUdaSelection(null);

    // Get auth type before clearing it - read it at the beginning so it's available in finally
    const currentAuthType = authType || localStorage.getItem(AUTH_TYPE_KEY);

    try {
      // Call logout API to invalidate session on server based on auth type
      // Use apufidc-auth for APUFIDC users
      if (currentAuthType === 'apufidc') {
        try {
          await baseAPIService.post('apufidc-auth/logout');
        } catch (error) {
          console.error('APUFIDC logout API error:', error);
        }
      } else if (currentAuthType === 'crda') {
        try {
          await baseAPIService.post('crda-auth/logout');
        } catch (error) {
          console.error('CRDA logout API error:', error);
        }
      } else {
        try {
          await baseAPIService.post('erp-auth/logout');
        } catch (error) {
          console.error('ERP logout API error:', error);
        }
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear all session cookies from all paths
      clearAllSessionCookies();

      // Clear all auth-related local storage
      clearAllAuthStorage();

      // Clear redirect URL so user doesn't get redirected to previous route after login
      localStorage.removeItem(REDIRECT_URL_KEY);

      setToken(null);
      setUser(null);
      setAuthType(null);

      // Redirect to appropriate login page based on auth type
      if (currentAuthType === 'apufidc') {
        window.location.href = '/app/apufidc/login';
      } else if (currentAuthType === 'crda') {
        window.location.href = '/app/use-cases/login';
      } else {
        window.location.href = '/login';
      }
    }
  };

  /**
   * Bind the session to a tenant (a UDA, for employees) and return the server's payload.
   * Callers decide what happens next: the header switcher swaps the data in place, while
   * the login UDA picker chains onward to the role picker or to that UDA's landing page.
   *
   * Deliberately does NOT touch the global `isLoading`: PrivateRoute unmounts the whole
   * route tree behind AppLoader while it is set, so flipping it here tore the page the
   * user was looking at off the screen just to change tenant. Every caller already shows
   * its own local busy state (the header dropdown, the login picker's per-row spinner).
   */
  /**
   * Bind the session to another tenant.
   *
   * `by` picks which identifier goes on the wire. 'uda' sends `uda_id`
   * (ds_uda_lst_t.uda_id) and is what the header UDA switcher uses — it is the id the
   * rest of the app scopes on. 'tenant' sends `tenant_id` (ds_tnt_lst_t.id) and is kept
   * for the login UDA picker and the legacy multi-tenant list. The two are DIFFERENT
   * numbering spaces (uda_id 3-24 vs tenant id 148-169), so passing one where the other
   * is expected selects the wrong UDA rather than failing.
   */
  const applyTenant = async (id: number, by: 'tenant' | 'uda' = 'tenant'): Promise<any> => {
    try {
      const payload = by === 'uda' ? { uda_id: id } : { tenant_id: id };
      const response: any = await baseAPIService.post('erp-auth/select-tenant', payload);

      if (response?.status === 200 && response?.data?.status === 200) {
        const resData = response?.data?.data;

        if (!resData?.user) {
          console.error('Invalid response structure:', resData);
          throw new Error('Invalid response from server');
        }

        // Prefer the body token for the login UDA switch. The header is still
        // supported for older server versions and normal header refreshes.
        const accessToken = resData.token || localStorage.getItem('x-access-token');
        if (accessToken) {
          localStorage.setItem('x-access-token', accessToken);
        }

        // Response structure matches login API (user object wrapper)
        const userData: User = {
          uid: resData.user.id,
          name: resData.user.name,
          username: resData.user.username ?? user?.username,
          roleId: resData.user.roleId,
          roleName: resData.user.roleName,
          roleType: resData.user.roleType,
          ulbId: resData.user.ulbId,
          udaId: resData.user.udaId,
          udaName: resData.user.udaName,
          udaCode: resData.user.udaCode,
          type: resData.user.type,
          lastLoginDate: user?.lastLoginDate, // Preserve last login date from original login
          tnt_usr_id: resData.user.tnt_usr_id,
          primaryAssignment: resData.user.primaryAssignment,
          primaryBoundary: resData.user.primaryBoundary,
          tenants: resData.tenants || user?.tenants || [],
          isStateRegionalUser: resData.user.isStateRegionalUser || false,
          tenantId: resData.user.tenantId,
        };

        // Store user data and assignments (token already saved by interceptor from response header)
        localStorage.setItem('user_data', JSON.stringify(userData));
        localStorage.setItem('user_assignments', JSON.stringify(resData.assignments || []));

        // Keep existing auth type when updating ULB schema
        const existingAuthType = localStorage.getItem(AUTH_TYPE_KEY) as 'erp' | 'crda' | null;
        if (existingAuthType) {
          localStorage.setItem(AUTH_TYPE_KEY, existingAuthType);
        }

        // Roles belong to the tenant, so switching UDA replaces the role list wholesale.
        if (resData.roles) {
          localStorage.setItem(USER_ROLES_KEY, JSON.stringify(resData.roles));
          setRoles(resData.roles);
        }
        // The new UDA's landing page, for whoever navigates after this call.
        if (resData.landingPageUrl && String(resData.landingPageUrl).startsWith('/app/')) {
          localStorage.setItem('landing_page_url', resData.landingPageUrl);
        }

        setToken(accessToken);
        setUser(userData);
        setAssignments(resData.assignments || []);
        if (existingAuthType) {
          setAuthType(existingAuthType);
        }

        return resData;

      } else {
        throw new Error(response?.data?.message || 'Failed to update ULB schema');
      }
    } catch (error: any) {
      console.error('Update ULB schema error:', error);
      throw new Error(error?.message || 'Failed to update ULB schema');
    }
  };

  const selectTenant = async (id: number, by: 'tenant' | 'uda' = 'tenant'): Promise<void> => {
    await applyTenant(id, by);
  };

  /**
   * Login UDA picker: bind the session to the chosen UDA, then continue the cascade —
   * if that UDA gives the employee more than one role, ask which; otherwise go straight in.
   */
  /**
   * Abandon the UDA question. The session at this point is bound to whichever UDA the
   * token defaulted to, so letting the user through would silently answer the question
   * for them — signing out is the honest outcome of "not now".
   */
  const cancelUdaSelection = async (): Promise<void> => {
    setPendingUdaSelection(null);
    await logout();
  };

  const selectUda = async (tenantId: number): Promise<void> => {
    const resData = await applyTenant(tenantId);

    if (resData?.requiresRoleSelection) {
      window.location.href = SELECT_ROLE_PATH;
      return;
    }
    window.location.href = localStorage.getItem('landing_page_url') || '/app/maud-erp';
  };

  /**
   * Apply an assignment (and optionally a specific role on it) to the session.
   * The server re-issues the JWT with that role's menu/setup/report profiles, so the
   * whole sidebar changes. `landOnRolePage` sends the user to the newly chosen role's
   * start page (used by the login picker); otherwise the current page is reloaded in
   * place (used by the header switcher).
   */
  const applyAssignment = async (assignmentId: number, roleId?: number, landOnRolePage = false) => {
    try {
      setIsLoading(true);

      const payload: Record<string, number> = { assignment_id: assignmentId };
      if (roleId) payload.role_id = roleId;

      const response: any = await baseAPIService.post('erp-auth/select-assignment', payload);

      if (response?.status === 200 && response?.data?.status === 200) {
        const resData = response?.data?.data;

        if (!resData?.user) {
          console.error('Invalid response structure:', resData);
          throw new Error('Invalid response from server');
        }

        // Token is set in response header and automatically saved by interceptor
        const accessToken = localStorage.getItem('x-access-token');

        // Update user data with new primary assignment
        const userData: User = {
          ...user!,
          roleId: resData.user.roleId,
          roleName: resData.user.roleName,
          roleType: resData.user.roleType,
          primaryAssignment: resData.user.primaryAssignment,
          tnt_usr_id: resData.user.tnt_usr_id,
        };

        // Store updated user data (token already saved by interceptor from response header)
        localStorage.setItem('user_data', JSON.stringify(userData));
        localStorage.setItem('user_assignments', JSON.stringify(resData.assignments || []));
        if (resData.roles) localStorage.setItem(USER_ROLES_KEY, JSON.stringify(resData.roles));

        // The chosen role has its own start page; keep it for redirectToUserMainPage()
        // and for any later refresh.
        const roleLandingUrl = resData.landingPageUrl;
        if (roleLandingUrl && roleLandingUrl.startsWith('/app/')) {
          localStorage.setItem('landing_page_url', roleLandingUrl);
        }

        setToken(accessToken);
        setUser(userData);
        setAssignments(resData.assignments || []);
        if (resData.roles) setRoles(resData.roles);

        // Menus and permissions are baked into the token, so a full document load is
        // required either way — either into the new role's start page, or in place.
        if (landOnRolePage) {
          window.location.href = localStorage.getItem('landing_page_url') || '/app/maud-erp';
        } else {
          window.location.reload();
        }

      } else {
        throw new Error(response?.data?.message || 'Failed to switch assignment');
      }
    } catch (error: any) {
      console.error('Assignment selection error:', error);
      throw new Error(error?.message || 'Failed to switch assignment');
    } finally {
      setIsLoading(false);
    }
  };

  const selectAssignment = (assignmentId: number, roleId?: number) => applyAssignment(assignmentId, roleId, false);

  // Login picker: pin the exact role AND land on that role's own start page.
  const selectRole = (assignmentId: number, roleId: number) => applyAssignment(assignmentId, roleId, true);

  const requestPasswordReset = async (data: ForgotPasswordPayload): Promise<{ success: boolean; message: string; otpRequired?: boolean; channel?: string }> => {
    try {
      const response: any = await baseAPIService.post('erp-auth/forgot-password', {
        username: data.username,
        email: data.email,
        mobileNumber: data.mobileNumber,
        whatsappNumber: data.whatsappNumber,
        captcha: data.captcha,
        captcha_id: data.captcha_id,
        channel: data.channel,
      });

      if (response?.status === 200 && response?.data?.status === 200) {
        // The service result (channel, otpRequired, message) is returned under data.data.
        const result = response?.data?.data || {};
        return {
          success: true,
          message: result.message || response?.data?.message || 'Password reset instructions have been sent to your email',
          otpRequired: !!result.otpRequired,
          channel: result.channel,
        };
      } else {
        throw new Error(response?.data?.err_message || response?.data?.message || 'Password reset request failed');
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Password reset request failed');
    }
  };

  const lookupForgotUser = async (
    username: string
  ): Promise<{ exists: boolean; email?: string; mobile?: string; name?: string }> => {
    try {
      const response: any = await baseAPIService.get(
        `erp-auth/forgot-password/lookup?username=${encodeURIComponent(username)}`
      );
      if (response?.status === 200 && response?.data?.status === 200) {
        return response.data.data || { exists: false };
      }
      return { exists: false };
    } catch (error) {
      console.error('Forgot-password user lookup error:', error);
      return { exists: false };
    }
  };

  const validateResetToken = async (token: string): Promise<boolean> => {
    try {
      const response: any = await baseAPIService.get(
        `erp-auth/reset-password/validate?token=${encodeURIComponent(token)}`
      );
      if (response?.status === 200 && response?.data?.status === 200) {
        return !!response?.data?.data?.valid;
      }
      return false;
    } catch (error) {
      console.error('Validate reset token error:', error);
      return false;
    }
  };

  const confirmPasswordReset = async (
    token: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response: any = await baseAPIService.post('erp-auth/reset-password/confirm', {
        token,
        newPassword,
      });

      if (response?.status === 200 && response?.data?.status === 200) {
        return {
          success: true,
          message: response?.data?.message || 'Your password has been reset successfully',
        };
      } else {
        throw new Error(response?.data?.err_message || response?.data?.message || 'Password reset failed');
      }
    } catch (error: any) {
      console.error('Confirm password reset error:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Password reset failed');
    }
  };

  // Step 1 of the OTP reset flow: verify the OTP (any channel) without consuming it.
  const verifyResetOtp = async (username: string, otp: string): Promise<boolean> => {
    try {
      const response: any = await baseAPIService.post('erp-auth/reset-password/verify-otp', {
        username,
        otp,
      });
      if (response?.status === 200 && response?.data?.status === 200) {
        return !!response?.data?.data?.valid;
      }
      throw new Error(response?.data?.err_message || response?.data?.message || 'OTP verification failed');
    } catch (error: any) {
      console.error('Verify reset OTP error:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'OTP verification failed');
    }
  };

  const resetPasswordByOtp = async (
    username: string,
    otp: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response: any = await baseAPIService.post('erp-auth/reset-password/confirm-otp', {
        username,
        otp,
        newPassword,
      });

      if (response?.status === 200 && response?.data?.status === 200) {
        return {
          success: true,
          message: response?.data?.message || 'Your password has been reset successfully',
        };
      } else {
        throw new Error(response?.data?.err_message || response?.data?.message || 'Password reset failed');
      }
    } catch (error: any) {
      console.error('OTP password reset error:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Password reset failed');
    }
  };

  const value = {
    user,
    token,
    pendingUdaSelection,
    isSelectingUda,
    cancelUdaSelection,
    isLoading,
    authType,
    login,
    loginCrda,
    loginApufidc,
    captcha,
    externalCaptcha,
    logout,
    selectTenant,
    selectUda,
    selectAssignment,
    assignments,
    roles,
    selectRole,
    requestPasswordReset,
    lookupForgotUser,
    validateResetToken,
    confirmPasswordReset,
    verifyResetOtp,
    resetPasswordByOtp,
    isAuthenticated: !!token && !!user,
    isCrdaAuthenticated: !!token && !!user && authType === 'crda',
    isApufidcAuthenticated: !!token && !!user && authType === 'apufidc',
    showPermissionDenied,
    hidePermissionDenied,
  };

  // Get permission label for display
  const getPermissionLabel = (permission?: string) => {
    switch (permission) {
      case 'create': return 'Create';
      case 'read': return 'Read';
      case 'update': return 'Update';
      case 'delete': return 'Delete';
      default: return permission || 'Access';
    }
  };

  const holders = permissionDeniedData?.holders;
  const hasHolders = holders && (
    (holders.usersWithPermission && holders.usersWithPermission.length > 0) ||
    (holders.adminUsers && holders.adminUsers.length > 0) ||
    (holders.rolesWithPermission && holders.rolesWithPermission.length > 0)
  );

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* Permission Denied Modal */}
      <Dialog
        open={permissionDeniedOpen}
        onClose={hidePermissionDenied}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon sx={{ color: 'white' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" color="warning.dark">
              Permission Denied
            </Typography>
          </Box>
          <IconButton onClick={hidePermissionDenied} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Message */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {permissionDeniedData?.message || 'You do not have permission to perform this action.'}
          </Typography>

          {/* Permission Info */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Required permission:
            </Typography>
            <Chip
              label={`${getPermissionLabel(permissionDeniedData?.permission)} - ${permissionDeniedData?.tableName || 'Unknown'}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Stack>

          {/* Success Message */}
          {requestSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {requestSuccess}
            </Alert>
          )}

          {/* Error Message */}
          {requestError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {requestError}
            </Alert>
          )}

          {/* Permission Holders */}
          {hasHolders && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1 }}>
                Contact these users for access:
              </Typography>

              {/* Users with Permission */}
              {holders?.usersWithPermission && holders.usersWithPermission.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <GroupIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Users with {getPermissionLabel(permissionDeniedData?.permission)} permission
                    </Typography>
                  </Stack>
                  <List dense disablePadding>
                    {holders.usersWithPermission.map((u) => (
                      <ListItem key={u.userId} disableGutters sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <PersonIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={u.userName}
                          secondary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <EmailIcon sx={{ fontSize: 12 }} />
                              <Typography variant="caption">{u.email}</Typography>
                              <Chip label={u.roleName} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                            </Stack>
                          }
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Admin Users */}
              {holders?.adminUsers && holders.adminUsers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <AdminIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Administrators
                    </Typography>
                  </Stack>
                  <List dense disablePadding>
                    {holders.adminUsers.map((u) => (
                      <ListItem key={u.userId} disableGutters sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <AdminIcon fontSize="small" color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={u.userName}
                          secondary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <EmailIcon sx={{ fontSize: 12 }} />
                              <Typography variant="caption">{u.email}</Typography>
                            </Stack>
                          }
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Roles with Permission */}
              {holders?.rolesWithPermission && holders.rolesWithPermission.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Roles with this permission:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {holders.rolesWithPermission.map((role) => (
                      <Chip
                        key={role.roleId}
                        label={role.roleName}
                        size="small"
                        variant="outlined"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={hidePermissionDenied} color="inherit">
            Close
          </Button>
          {holders?.canRequestAccess && !requestSuccess && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<SendIcon />}
              onClick={() => setRequestDialogOpen(true)}
            >
              Request Access
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Request Access Dialog */}
      <Dialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Request <strong>{getPermissionLabel(permissionDeniedData?.permission)}</strong> permission
            for <strong>{permissionDeniedData?.tableName}</strong>.
            An email will be sent to the administrators.
          </Typography>
          <TextField
            label="Reason for Request"
            multiline
            rows={3}
            value={requestReason}
            onChange={(e) => setRequestReason(e.target.value)}
            fullWidth
            placeholder="Please explain why you need this access..."
            helperText="Optional: Provide a reason to help administrators understand your request"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)} disabled={requestSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRequestAccess}
            disabled={requestSubmitting}
            startIcon={requestSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {requestSubmitting ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};
